import { useState, useCallback, useEffect } from "react";
import type { ConnectState, Provider } from "../types";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("token");
}

function getParentOrigin(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("origin");
}

const parentOrigin = getParentOrigin();

function postToParent(type: string, payload?: Record<string, unknown>) {
  if (window.parent !== window && parentOrigin) {
    window.parent.postMessage({ type, payload }, parentOrigin);
  }
}

export function useConnect() {
  const [state, setState] = useState<ConnectState>({
    step: "init",
    session: null,
    selectedProvider: null,
    selectedState: null,
    credentials: {},
    error: null,
    publicToken: null,
    connectionId: null,
    submitting: false,
  });

  // Initialize the connect session
  useEffect(() => {
    const token = getTokenFromUrl();
    if (!token) {
      setState((s) => ({
        ...s,
        step: "error",
        error: "Missing link token. Please restart the connection flow.",
      }));
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    fetch(`${API_BASE}/v1/connect/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ link_token: token }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.error?.message || "Failed to initialize");
        }
        return res.json();
      })
      .then((res) => {
        if (res.error) {
          setState((s) => ({
            ...s,
            step: "error",
            error: res.error.message || "Failed to initialize",
          }));
          return;
        }
        setState((s) => ({
          ...s,
          step: "intro",
          session: {
            sessionId: res.data.session_id,
            organizationName: res.data.organization_name,
            organizationLogoUrl: res.data.organization_logo_url ?? null,
            providers: res.data.providers,
            isSandbox: res.data.is_sandbox ?? false,
          },
        }));
        postToParent("seedlink:ready");
      })
      .catch((err) => {
        const message = err.name === "AbortError"
          ? "Request timed out. Please try again."
          : err.message || "Unable to connect to Seedlink. Please try again.";
        setState((s) => ({
          ...s,
          step: "error",
          error: message,
        }));
      })
      .finally(() => {
        clearTimeout(timeout);
      });
  }, []);

  const continueFromIntro = useCallback(() => {
    setState((s) => ({ ...s, step: "select_provider" }));
  }, []);

  const selectProvider = useCallback((provider: Provider) => {
    setState((s) => ({
      ...s,
      selectedProvider: provider,
      selectedState: null,
      credentials: {},
      step: "select_state",
    }));
    postToParent("seedlink:event", { type: "PROVIDER_SELECTED", provider: provider.id });
  }, []);

  const selectState = useCallback((stateCode: string) => {
    setState((s) => ({ ...s, selectedState: stateCode }));
    postToParent("seedlink:event", { type: "STATE_SELECTED", state: stateCode });
  }, []);

  const continueToCredentials = useCallback(() => {
    setState((s) => (s.selectedState ? { ...s, step: "enter_credentials" } : s));
  }, []);

  const updateCredential = useCallback((field: string, value: string) => {
    setState((s) => ({
      ...s,
      credentials: { ...s.credentials, [field]: value },
    }));
  }, []);

  const goBack = useCallback(() => {
    setState((s) => {
      if (s.step === "select_provider") return { ...s, step: "intro" };
      if (s.step === "select_state") return { ...s, step: "select_provider" };
      if (s.step === "enter_credentials") return { ...s, step: "select_state" };
      return s;
    });
  }, []);

  const retryCredentials = useCallback(() => {
    setState((s) => ({ ...s, step: "enter_credentials", error: null }));
  }, []);

  const submitCredentials = useCallback(async () => {
    setState((s) => ({ ...s, error: null, submitting: true }));
    postToParent("seedlink:event", { type: "CREDENTIALS_SUBMITTED" });
    postToParent("seedlink:event", { type: "VALIDATION_STARTED" });

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    try {
      const res = await fetch(`${API_BASE}/v1/connect/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: state.session?.sessionId,
          provider: state.selectedProvider?.id,
          state: state.selectedState,
          credentials: state.credentials,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        throw new Error(errorData?.error?.message || "Validation failed");
      }

      const data = await res.json();

      if (data.error) {
        postToParent("seedlink:event", { type: "VALIDATION_COMPLETE", success: false });
        setState((s) => ({
          ...s,
          step: "failed",
          error: data.error.message || "Validation failed",
          submitting: false,
        }));
        return;
      }

      if (data.data.status === "invalid") {
        postToParent("seedlink:event", { type: "VALIDATION_COMPLETE", success: false });
        setState((s) => ({
          ...s,
          step: "failed",
          error: data.data.error_message || "Invalid credentials. Please try again.",
          submitting: false,
        }));
        return;
      }

      postToParent("seedlink:event", { type: "VALIDATION_COMPLETE", success: true });

      setState((s) => ({
        ...s,
        step: "success",
        publicToken: data.data.public_token,
        connectionId: data.data.connection_id,
        submitting: false,
      }));

      postToParent("seedlink:success", {
        public_token: data.data.public_token,
        metadata: {
          provider: state.selectedProvider?.id,
          state: state.selectedState,
          connection_id: data.data.connection_id,
          is_sandbox: state.session?.isSandbox ?? false,
        },
      });

      setTimeout(() => {
        postToParent("seedlink:exit", {});
      }, 2000);
    } catch (err) {
      const message = err instanceof Error && err.name === "AbortError"
        ? "Request timed out. Please try again."
        : err instanceof Error ? err.message : "Network error. Please check your connection and try again.";
      postToParent("seedlink:event", { type: "VALIDATION_COMPLETE", success: false });
      setState((s) => ({ ...s, step: "failed", error: message, submitting: false }));
    } finally {
      clearTimeout(timeout);
    }
  }, [state.session, state.selectedProvider, state.selectedState, state.credentials]);

  const close = useCallback(() => {
    postToParent("seedlink:exit", {});
  }, []);

  const isSandbox = state.session?.isSandbox ?? false;

  return {
    state,
    isSandbox,
    continueFromIntro,
    selectProvider,
    selectState,
    continueToCredentials,
    updateCredential,
    goBack,
    retryCredentials,
    submitCredentials,
    close,
  };
}
